/**
 * Modal d'historique des imports de scans (unitaires + en lot).
 * Filtres : type (devoirs/correction), mode (single/batch), date.
 * Détails fichier-par-fichier dépliables par ligne.
 */
import React, { useState } from 'react';
import {
  X, Clock, User, FileText, ChevronDown, ChevronRight,
  Download, RefreshCw, Loader, Filter, CheckCircle2, XCircle, AlertTriangle, FileCheck, FileX,
} from 'lucide-react';
import {
  useGetHistoriqueImportsQuery,
  HistoriqueImportScan,
  FichierBatchQRResult,
  StatutFichierBatchQR,
} from '../../services/ocrApi';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Si fourni, le filtre type est verrouillé sur cette valeur. */
  typeFilter?: 'devoirs' | 'correction';
}

const STATUT_BADGE: Record<StatutFichierBatchQR, { label: string; cls: string; icon: React.ReactNode }> = {
  success: { label: 'Uploadé', cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300', icon: <CheckCircle2 size={11} /> },
  remplace: { label: 'Remplacé', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300', icon: <RefreshCw size={11} /> },
  deja_uploade: { label: 'Déjà uploadé', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300', icon: <FileCheck size={11} /> },
  deja_traite: { label: 'Déjà traité', cls: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300', icon: <FileX size={11} /> },
  non_reconnu: { label: 'Non reconnu', cls: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300', icon: <AlertTriangle size={11} /> },
  erreur: { label: 'Erreur', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300', icon: <XCircle size={11} /> },
};

const HistoriqueImportsModal: React.FC<Props> = ({ open, onClose, typeFilter }) => {
  const [type, setType] = useState<'tous' | 'devoirs' | 'correction'>(typeFilter || 'tous');
  const [mode, setMode] = useState<'tous' | 'single' | 'batch'>('tous');
  const [dateMin, setDateMin] = useState('');
  const [dateMax, setDateMax] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const { data, isFetching, refetch } = useGetHistoriqueImportsQuery(
    open ? {
      ...(type !== 'tous' ? { type_releve: type } : {}),
      ...(mode !== 'tous' ? { mode } : {}),
      ...(dateMin ? { date_min: dateMin } : {}),
      ...(dateMax ? { date_max: dateMax } : {}),
      limit: 200,
    } : undefined,
    { skip: !open }
  );

  const exportCSV = () => {
    if (!data) return;
    const cols = [
      'date', 'utilisateur', 'type', 'mode', 'fichiers',
      'success', 'remplace', 'deja_uploade', 'deja_traite', 'non_reconnu', 'erreur',
      'releve_code', 'releve_matiere',
    ];
    const rows = [
      cols.join(';'),
      ...data.results.map(h => [
        new Date(h.date_upload).toLocaleString('fr-FR'),
        h.utilisateur_nom,
        h.type_releve_label,
        h.mode_label,
        h.nombre_fichiers,
        h.nombre_success,
        h.nombre_remplace,
        h.nombre_deja_uploade,
        h.nombre_deja_traite,
        h.nombre_non_reconnu,
        h.nombre_erreur,
        h.releve_code || '',
        h.releve_matiere || '',
      ].map(v => String(v ?? '').replace(/[;\n\r]/g, ' ')).join(';')),
    ];
    const csv = '﻿' + rows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    a.href = url;
    a.download = `historique_imports_${ts}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-5 py-3 border-b dark:border-gray-700 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Clock size={20} className="text-indigo-600" />
            Historique des imports de scans
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
          >
            <X size={22} />
          </button>
        </div>

        {/* Filtres */}
        <div className="px-5 py-3 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1.5">
            <Filter size={14} className="text-gray-500" />
            <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Filtres :</span>
          </div>
          {!typeFilter && (
            <select
              value={type}
              onChange={(e) => setType(e.target.value as any)}
              className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="tous">Tous types</option>
              <option value="devoirs">Devoirs</option>
              <option value="correction">Examens</option>
            </select>
          )}
          <select
            value={mode}
            onChange={(e) => setMode(e.target.value as any)}
            className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            <option value="tous">Tous modes</option>
            <option value="single">Unitaire</option>
            <option value="batch">Lot (QR auto)</option>
          </select>
          <div className="flex items-center gap-1 text-xs">
            <span className="text-gray-600 dark:text-gray-400">Du</span>
            <input
              type="date"
              value={dateMin}
              onChange={(e) => setDateMin(e.target.value)}
              className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
            <span className="text-gray-600 dark:text-gray-400">au</span>
            <input
              type="date"
              value={dateMax}
              onChange={(e) => setDateMax(e.target.value)}
              className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>
          <button
            onClick={() => refetch()}
            className="ml-auto flex items-center gap-1 px-2 py-1 text-xs bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600"
          >
            <RefreshCw size={12} className={isFetching ? 'animate-spin' : ''} /> Actualiser
          </button>
          <button
            onClick={exportCSV}
            disabled={!data || data.results.length === 0}
            className="flex items-center gap-1 px-2 py-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded disabled:opacity-50"
          >
            <Download size={12} /> Export CSV
          </button>
        </div>

        {/* Liste */}
        <div className="flex-1 overflow-y-auto p-3">
          {isFetching && !data ? (
            <div className="text-center py-12">
              <Loader className="animate-spin mx-auto text-indigo-600" size={32} />
              <p className="mt-2 text-sm text-gray-500">Chargement…</p>
            </div>
          ) : !data || data.results.length === 0 ? (
            <div className="text-center py-12 text-sm text-gray-500">
              Aucun import dans l'historique avec ces filtres.
            </div>
          ) : (
            <div className="space-y-1.5">
              {data.results.map((h) => (
                <ImportRow
                  key={h.id}
                  item={h}
                  expanded={expandedId === h.id}
                  onToggle={() => setExpandedId(expandedId === h.id ? null : h.id)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-2.5 border-t dark:border-gray-700 flex items-center justify-between text-xs">
          <span className="text-gray-500 dark:text-gray-400">
            {data ? `${data.count} entrée${data.count > 1 ? 's' : ''} affichée${data.count > 1 ? 's' : ''}` : ''}
          </span>
          <button
            onClick={onClose}
            className="px-4 py-1.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-300"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

const ImportRow: React.FC<{
  item: HistoriqueImportScan;
  expanded: boolean;
  onToggle: () => void;
}> = ({ item, expanded, onToggle }) => {
  const successCount = item.nombre_success + item.nombre_remplace;
  const issueCount = item.nombre_deja_uploade + item.nombre_deja_traite + item.nombre_non_reconnu + item.nombre_erreur;

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded">
      <button
        onClick={onToggle}
        className="w-full px-3 py-2 flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-700/30 text-left"
      >
        {expanded ? <ChevronDown size={14} className="text-gray-400 shrink-0" /> : <ChevronRight size={14} className="text-gray-400 shrink-0" />}
        <span className="text-xs text-gray-600 dark:text-gray-400 font-mono w-32 shrink-0">
          {new Date(item.date_upload).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
        </span>
        <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${
          item.type_releve === 'devoirs'
            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
            : 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300'
        }`}>
          {item.type_releve_label}
        </span>
        <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${
          item.mode === 'batch'
            ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
            : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
        }`}>
          {item.mode_label}
        </span>
        <span className="text-xs text-gray-700 dark:text-gray-300 flex items-center gap-1">
          <FileText size={11} /> {item.nombre_fichiers}
        </span>
        {successCount > 0 && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
            {successCount} OK
          </span>
        )}
        {issueCount > 0 && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
            {issueCount} pb
          </span>
        )}
        {item.releve_code && (
          <span className="text-xs text-gray-500 dark:text-gray-400 truncate ml-auto pr-2">
            {item.releve_code}{item.releve_matiere ? ` · ${item.releve_matiere}` : ''}
          </span>
        )}
        <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1 shrink-0">
          <User size={11} /> {item.utilisateur_nom || '—'}
        </span>
      </button>

      {expanded && (
        <div className="px-4 py-2 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30 space-y-2">
          {/* Résumé */}
          <div className="flex flex-wrap gap-1.5 text-[10px]">
            {[
              { val: item.nombre_success, label: 'Uploadé', k: 'success' as const },
              { val: item.nombre_remplace, label: 'Remplacé', k: 'remplace' as const },
              { val: item.nombre_deja_uploade, label: 'Déjà uploadé', k: 'deja_uploade' as const },
              { val: item.nombre_deja_traite, label: 'Déjà traité', k: 'deja_traite' as const },
              { val: item.nombre_non_reconnu, label: 'Non reconnu', k: 'non_reconnu' as const },
              { val: item.nombre_erreur, label: 'Erreur', k: 'erreur' as const },
            ].filter(x => x.val > 0).map(x => (
              <span key={x.k} className={`px-1.5 py-0.5 rounded font-semibold ${STATUT_BADGE[x.k].cls}`}>
                {x.label} : {x.val}
              </span>
            ))}
            {item.remplacer_actif && (
              <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                Mode "Remplacer" actif
              </span>
            )}
          </div>

          {/* Détails par fichier */}
          {item.details && item.details.length > 0 ? (
            <div className="space-y-1">
              {item.details.map((d: FichierBatchQRResult, idx: number) => {
                const badge = STATUT_BADGE[d.statut as StatutFichierBatchQR] || STATUT_BADGE.erreur;
                return (
                  <div
                    key={idx}
                    className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded px-2 py-1.5 text-[11px]"
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-semibold ${badge.cls}`}>
                        {badge.icon} {badge.label}
                      </span>
                      <span className="font-mono text-gray-700 dark:text-gray-300 truncate max-w-[260px]">
                        {d.fichier}
                      </span>
                      {d.code_releve && (
                        <span className="text-gray-500 dark:text-gray-400">
                          → <span className="font-mono">{d.code_releve}</span>
                        </span>
                      )}
                      {d.matiere_code && (
                        <span className="text-gray-500 dark:text-gray-400">
                          {d.matiere_code} {d.matiere_nom ? `(${d.matiere_nom})` : ''}
                        </span>
                      )}
                      {d.enseignant_nom && (
                        <span className="text-gray-500 dark:text-gray-400">
                          · {d.enseignant_nom}
                        </span>
                      )}
                    </div>
                    {d.message && (
                      <div className="mt-0.5 text-gray-600 dark:text-gray-400 ml-1">
                        {d.message}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-xs text-gray-400 italic">Pas de détail disponible.</div>
          )}
        </div>
      )}
    </div>
  );
};

export default HistoriqueImportsModal;
